/*
 * COFFEE ATTRIBUTE DRAWER MOTION STORYBOARD
 *
 *    0ms  backdrop resolves while the panel scales gently from its centred position
 *  300ms  panel settles at full opacity and focus moves to the close control
 *
 *    0ms  close starts from the current visual position
 *  220ms  panel softens and retreats, then trigger focus is restored
 */
const ATTRIBUTE_DRAWER_MOTION = Object.freeze({
  enterDuration: 300,
  exitDuration: 220,
  popoverEnterScale: 0.97,
  popoverExitScale: 0.985,
  enterEasing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  exitEasing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
});

const ATTRIBUTE_HINT_STORAGE_KEY = 'sigma:coffee-attribute-hint:v2';
const ATTRIBUTE_HINT_ACKNOWLEDGED = 'acknowledged';
const attributeHint = document.querySelector('[data-product-attributes-hint]');
const attributeHintClose = attributeHint?.querySelector('[data-product-attributes-hint-close]');
const attributeInfoTriggers = document.querySelectorAll('[data-product-attribute-info-trigger]');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const hasAcknowledgedAttributeHint = () => {
  try {
    return window.localStorage.getItem(ATTRIBUTE_HINT_STORAGE_KEY) === ATTRIBUTE_HINT_ACKNOWLEDGED;
  } catch {
    return false;
  }
};

const acknowledgeAttributeHint = () => {
  try {
    window.localStorage.setItem(ATTRIBUTE_HINT_STORAGE_KEY, ATTRIBUTE_HINT_ACKNOWLEDGED);
  } catch {
    // Storage can be unavailable in privacy modes. The hint remains dismissible for this page view.
  }
};

const showAttributeHint = () => {
  if (!(attributeHint instanceof HTMLElement) || hasAcknowledgedAttributeHint()) {
    return;
  }

  attributeHint.hidden = false;
  attributeInfoTriggers.forEach((trigger) => {
    trigger.setAttribute('aria-describedby', attributeHint.dataset.descriptionId);
  });

  if (reducedMotion.matches) {
    attributeHint.classList.add('is-visible');
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => attributeHint.classList.add('is-visible'));
  });
};

const dismissAttributeHint = ({ remember = true } = {}) => {
  if (!(attributeHint instanceof HTMLElement) || attributeHint.hidden) {
    return;
  }

  if (remember) {
    acknowledgeAttributeHint();
  }

  attributeInfoTriggers.forEach((trigger) => {
    trigger.removeAttribute('aria-describedby');
  });
  attributeHint.classList.remove('is-visible');

  if (reducedMotion.matches) {
    attributeHint.hidden = true;
    return;
  }

  window.setTimeout(() => {
    if (!attributeHint.classList.contains('is-visible')) {
      attributeHint.hidden = true;
    }
  }, 200);
};

attributeHintClose?.addEventListener('click', () => dismissAttributeHint());
showAttributeHint();

const drawerControllers = new Map();
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const visibleFocusableElements = (container) =>
  [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
    (element) =>
      element instanceof HTMLElement &&
      element.getAttribute('aria-disabled') !== 'true' &&
      element.getAttribute('aria-hidden') !== 'true' &&
      !element.closest('[inert]') &&
      element.getClientRects().length > 0,
  );

document.querySelectorAll('[data-animated-details]').forEach((details) => {
  if (!(details instanceof HTMLDetailsElement)) return;

  const summary = details.querySelector(':scope > summary');
  const content = details.querySelector('[data-animated-details-content]');
  if (!(summary instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

  // Match the Library disclosure: intrinsic grid track, unclipped inner padding.
  const motion = document.createElement('div');
  const reveal = document.createElement('div');
  motion.className = 'product-variety-drawer__disclosure-motion';
  reveal.className = 'product-variety-drawer__disclosure-reveal';
  content.before(motion);
  motion.append(reveal);
  reveal.append(content);
  details.dataset.detailsState = details.open ? 'open' : 'closed';
  motion.inert = !details.open;
  let frame = 0;
  let closeTimer = 0;

  const finishClose = () => {
    if (details.dataset.detailsState !== 'closing') return;
    window.clearTimeout(closeTimer);
    details.open = false;
    details.dataset.detailsState = 'closed';
  };

  const toggleDetails = (immediate) => {
    const state = details.dataset.detailsState;
    const open = state === 'closed' || state === 'closing';
    window.cancelAnimationFrame(frame);
    window.clearTimeout(closeTimer);
    motion.inert = !open;
    details.toggleAttribute('data-details-instant', immediate || reducedMotion.matches);

    if (immediate || reducedMotion.matches) {
      details.open = open;
      details.dataset.detailsState = open ? 'open' : 'closed';
      return;
    }

    if (open) {
      const wasClosed = !details.open;
      details.open = true;
      if (wasClosed) {
        details.dataset.detailsState = 'opening';
        motion.getBoundingClientRect();
        frame = window.requestAnimationFrame(() => {
          details.dataset.detailsState = 'open';
        });
      } else {
        // A closing track retargets from its current interpolated size.
        details.dataset.detailsState = 'open';
      }
    } else {
      details.dataset.detailsState = 'closing';
      closeTimer = window.setTimeout(finishClose, 240);
    }
  };

  summary.addEventListener('click', (event) => {
    event.preventDefault();
    toggleDetails(event.detail === 0);
  });

  motion.addEventListener('transitionend', (event) => {
    if (event.target === motion && event.propertyName === 'grid-template-rows') finishClose();
  });

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    window.cancelAnimationFrame(frame);
    if (details.dataset.detailsState === 'opening') details.dataset.detailsState = 'open';
    finishClose();
  });
});

const currentFrame = (element) => {
  const style = window.getComputedStyle(element);

  return {
    opacity: style.opacity,
    transform: style.transform === 'none' ? 'translate3d(0, 0, 0)' : style.transform,
  };
};

const animateDialogBackdrop = (dialog, fallback, keyframes, options) => {
  try {
    return dialog.animate(keyframes, { ...options, pseudoElement: '::backdrop' });
  } catch {
    // Older engines still get the native, full-viewport backdrop without a fade.
    return fallback.animate(keyframes, options);
  }
};

const panelOffset = (opening) => {
  const scale = opening
    ? ATTRIBUTE_DRAWER_MOTION.popoverEnterScale
    : ATTRIBUTE_DRAWER_MOTION.popoverExitScale;

  return `translate3d(0, 0, 0) scale(${scale})`;
};

const setButtonLoading = (button, loading) => {
  button?.dispatchEvent(
    new CustomEvent('sigma:button-loading', {
      bubbles: true,
      detail: { loading },
    }),
  );
};

document.querySelectorAll('[data-product-attribute-dialog]').forEach((dialog) => {
  if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') {
    return;
  }

  const drawerType = dialog.dataset.productAttributeDialog;
  const panel = dialog.querySelector('[data-product-attribute-panel]');
  const backdrop = dialog.querySelector('[data-product-attribute-dismiss]');

  if (!drawerType || !(panel instanceof HTMLElement) || !(backdrop instanceof HTMLElement)) {
    return;
  }

  let activeTrigger = null;
  let panelAnimation = null;
  let backdropAnimation = null;
  let closeTimer = 0;
  let syncGoldCoverConfirmation = null;

  const setExpanded = (expanded) => {
    document
      .querySelectorAll(`[data-product-attribute-trigger="${drawerType}"]`)
      .forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded && trigger === activeTrigger));
      });
  };

  const scenes = [...dialog.querySelectorAll(
    '.product-origin-drawer__process-art, .product-variety-drawer__art, .product-origin-drawer__map-art',
  )];
  const visibleScenes = new Set();
  let keyboardOpening = false;
  const syncSceneMotion = () => {
    const isStatic = reducedMotion.matches || keyboardOpening;
    const canRun = dialog.open && !document.hidden && !isStatic &&
      dialog.dataset.attributeState !== 'closing';
    scenes.forEach((scene) => {
      scene.dataset.sceneMotion = isStatic ? 'static'
        : canRun && visibleScenes.has(scene) && scene.getClientRects().length > 0 ? 'running' : 'paused';
    });
  };
  const sceneObserver = scenes.length && typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
          visibleScenes.add(entry.target);
        } else {
          visibleScenes.delete(entry.target);
        }
      });
      syncSceneMotion();
    }, { root: panel, threshold: [0, 0.15] })
    : null;

  const originTabs = dialog.querySelector('[data-product-origin-tabs]');
  const countryTabs = [...dialog.querySelectorAll('[data-product-origin-tab]')];
  const countryPanels = countryTabs.map((tab) =>
    [...dialog.querySelectorAll('[data-product-origin-country]')].find(
      (country) => country.id === tab.getAttribute('aria-controls'),
    ),
  );

  if (originTabs && countryTabs.length > 1 && countryPanels.every(Boolean)) {
    const selectCountry = (index, moveFocus = false) => {
      countryTabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        countryPanels[tabIndex].hidden = !selected;
      });
      if (moveFocus) countryTabs[index].focus();
      syncSceneMotion();
    };

    countryPanels.forEach((country, index) => {
      country.setAttribute('role', 'tabpanel');
      country.setAttribute('aria-labelledby', countryTabs[index].id);
      country.tabIndex = 0;
    });
    countryTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectCountry(index));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;
        const rtl = window.getComputedStyle(originTabs).direction === 'rtl';
        if (event.key === 'ArrowRight') nextIndex += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') nextIndex += rtl ? 1 : -1;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = countryTabs.length - 1;
        else return;
        event.preventDefault();
        selectCountry((nextIndex + countryTabs.length) % countryTabs.length, true);
      });
    });
    selectCountry(0);
    originTabs.hidden = false;
    dialog.dataset.productOriginTabsReady = 'true';
  }

  const runMotion = (opening, immediate = false) => {
    // Read before cancelling. Cancelling first snaps an interrupted entrance to
    // its settled style and makes the close animation flash at full opacity.
    const panelFrom = opening && !panelAnimation
      ? { opacity: 0, transform: panelOffset(true) }
      : currentFrame(panel);
    const backdropFrom = opening && !backdropAnimation
      ? 0
      : window.getComputedStyle(dialog, '::backdrop').opacity;

    panelAnimation?.cancel();
    backdropAnimation?.cancel();
    panelAnimation = null;
    backdropAnimation = null;

    if (immediate || reducedMotion.matches || typeof panel.animate !== 'function') {
      return null;
    }

    const panelTo = opening
      ? { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      : { opacity: 0, transform: panelOffset(false) };
    const duration = opening
      ? ATTRIBUTE_DRAWER_MOTION.enterDuration
      : ATTRIBUTE_DRAWER_MOTION.exitDuration;
    const easing = opening
      ? ATTRIBUTE_DRAWER_MOTION.enterEasing
      : ATTRIBUTE_DRAWER_MOTION.exitEasing;

    panelAnimation = panel.animate([panelFrom, panelTo], {
      duration,
      easing,
      fill: 'both',
    });
    backdropAnimation = animateDialogBackdrop(
      dialog,
      backdrop,
      [
        { opacity: backdropFrom },
        { opacity: opening ? 1 : 0 },
      ],
      {
        duration,
        easing,
        fill: 'both',
      },
    );

    return panelAnimation;
  };

  const finishClose = (animation = null) => {
    window.clearTimeout(closeTimer);

    if (animation && panelAnimation !== animation) {
      return;
    }

    panelAnimation?.cancel();
    backdropAnimation?.cancel();
    panelAnimation = null;
    backdropAnimation = null;
    dialog.close();
  };

  const closeDrawer = ({ immediate = false } = {}) => {
    if (!dialog.open) return;
    if (dialog.dataset.attributeState === 'closing') {
      if (immediate) finishClose();
      return;
    }

    dialog.dataset.attributeState = 'closing';
    syncSceneMotion();
    const animation = runMotion(false, immediate);

    if (!animation) {
      finishClose();
      return;
    }

    animation.finished.then(() => finishClose(animation)).catch(() => {});
    closeTimer = window.setTimeout(
      () => finishClose(animation),
      ATTRIBUTE_DRAWER_MOTION.exitDuration + 80,
    );
  };

  const openDrawer = (trigger, { immediate = false } = {}) => {
    window.clearTimeout(closeTimer);
    dismissAttributeHint();
    activeTrigger = trigger;
    keyboardOpening = immediate;

    if (!dialog.open) {
      dialog.showModal();
    }

    panel.scrollTop = 0;
    delete dialog.dataset.attributeState;
    scenes.forEach((scene) => {
      if (sceneObserver) sceneObserver.observe(scene);
      else visibleScenes.add(scene);
    });
    syncSceneMotion();
    setExpanded(true);
    syncGoldCoverConfirmation?.();
    dialog.querySelector('[data-product-attribute-close]')?.focus({ preventScroll: true });

    const animation = runMotion(true, immediate);
    animation?.finished
      .then(() => {
        if (panelAnimation === animation) {
          animation.cancel();
          backdropAnimation?.cancel();
          panelAnimation = null;
          backdropAnimation = null;
        }
      })
      .catch(() => {});
  };

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDrawer({ immediate: true });
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = visibleFocusableElements(dialog);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements.at(-1);

    if (!firstFocusable || !lastFocusable) {
      return;
    }

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  });

  dialog.addEventListener('close', () => {
    window.clearTimeout(closeTimer);
    sceneObserver?.disconnect();
    visibleScenes.clear();
    syncSceneMotion();
    delete dialog.dataset.attributeState;
    setExpanded(false);
    activeTrigger?.focus?.({ preventScroll: true });
    activeTrigger = null;
  });

  reducedMotion.addEventListener('change', () => {
    syncSceneMotion();
    if (!reducedMotion.matches) return;
    if (dialog.dataset.attributeState === 'closing') {
      finishClose();
    } else {
      panelAnimation?.cancel();
      backdropAnimation?.cancel();
      panelAnimation = null;
      backdropAnimation = null;
    }
  });

  if (drawerType === 'gold-cover' && dialog.hasAttribute('data-gold-cover-confirmation-dialog')) {
    const formId = dialog.dataset.goldCoverFormId;
    const form = formId ? document.getElementById(formId) : null;
    const productRoot = dialog.closest('[data-product-section]');
    const originalButton = productRoot?.querySelector('[data-gold-cover-add]');
    const goldCover = originalButton?.closest('[data-gold-cover]');
    const confirmButton = dialog.querySelector('[data-gold-cover-confirm]');
    const confirmLabel = confirmButton?.querySelector('[data-gold-cover-confirm-label]');
    const terms = dialog.querySelector('[data-gold-cover-terms]');
    const dialogError = dialog.querySelector('[data-gold-cover-dialog-error]');
    const availability = dialog.querySelector('[data-gold-cover-dialog-availability]');
    const formError = form?.querySelector('[data-product-form-error]');

    if (
      form instanceof HTMLFormElement
      && originalButton instanceof HTMLButtonElement
      && goldCover instanceof HTMLElement
      && confirmButton instanceof HTMLButtonElement
      && confirmLabel instanceof HTMLElement
      && (!terms || terms instanceof HTMLInputElement)
      && (!terms || terms.form === form)
    ) {
      let pending = false;
      let sawBusy = false;
      let modalLoading = false;
      let focusErrorFrame = 0;

      const recoveryRequired = () =>
        formError instanceof HTMLElement && formError.hasAttribute('data-cart-recovery-error');

      const focusDialogError = () => {
        window.cancelAnimationFrame(focusErrorFrame);
        focusErrorFrame = window.requestAnimationFrame(() => {
          focusErrorFrame = 0;
          if (!dialog.open || !(dialogError instanceof HTMLElement) || dialogError.hidden) return;
          dialogError.focus({ preventScroll: true });
        });
      };

      const showDialogError = (message = '') => {
        if (!(dialogError instanceof HTMLElement)) return;
        const nextMessage = String(message).trim();
        if (dialogError.textContent !== nextMessage) dialogError.textContent = nextMessage;
        const nextHidden = !nextMessage;
        if (dialogError.hidden !== nextHidden) dialogError.hidden = nextHidden;
      };

      const syncAvailability = (unavailable) => {
        if (!(availability instanceof HTMLElement)) return;
        const nextHidden = !unavailable;
        if (availability.hidden !== nextHidden) availability.hidden = nextHidden;
        if (!unavailable) return;
        const nextMessage = dialog.dataset.goldCoverAvailable === 'true'
          ? 'Gold Cover can’t be added with the selected machine option.'
          : 'Gold Cover is currently unavailable for this machine.';
        if (availability.textContent !== nextMessage) availability.textContent = nextMessage;
      };

      const syncModalLoading = (loading) => {
        if (modalLoading === loading) return;
        modalLoading = loading;
        setButtonLoading(confirmButton, loading);
      };

      const syncRecoveryAction = (required) => {
        const nextLabel = required ? 'Reload page' : 'Add Gold Cover';
        if (confirmLabel.textContent !== nextLabel) confirmLabel.textContent = nextLabel;
      };

      syncGoldCoverConfirmation = () => {
        const busy = form.hasAttribute('aria-busy');
        const added = originalButton.hasAttribute('data-gold-cover-added');
        const unavailable = originalButton.disabled && !busy && !added;
        const termsMissing = terms instanceof HTMLInputElement && !terms.checked;
        const mustReload = recoveryRequired();

        if (pending && busy) sawBusy = true;

        if (pending && added) {
          pending = false;
          syncModalLoading(false);
          if (!confirmButton.disabled) confirmButton.disabled = true;
          closeDrawer();
          return;
        }

        if (pending && sawBusy && !busy) {
          const message = formError?.textContent?.trim()
            || 'We couldn’t add Gold Cover. Please try again.';
          pending = false;
          showDialogError(message);
          focusDialogError();
        }

        syncAvailability(unavailable);
        syncModalLoading(pending && busy);
        syncRecoveryAction(mustReload);
        const confirmDisabled = mustReload
          ? false
          : termsMissing || busy || added || unavailable;
        if (confirmButton.disabled !== confirmDisabled) confirmButton.disabled = confirmDisabled;
      };

      terms?.addEventListener('change', () => {
        showDialogError();
        syncGoldCoverConfirmation();
      });

      confirmButton.addEventListener('click', () => {
        if (recoveryRequired()) {
          window.location.reload();
          return;
        }

        if (originalButton.hasAttribute('data-gold-cover-added')) return;

        if (originalButton.disabled) {
          syncAvailability(true);
          return;
        }

        if (terms instanceof HTMLInputElement && !terms.checked) {
          terms.focus();
          return;
        }

        if (!form.checkValidity()) {
          form.reportValidity();
          showDialogError('Check the product options, then try again.');
          return;
        }

        showDialogError();
        pending = true;
        sawBusy = false;
        syncModalLoading(true);
        if (!confirmButton.disabled) confirmButton.disabled = true;
        form.requestSubmit(originalButton);
        syncGoldCoverConfirmation();

        if (pending && !form.hasAttribute('aria-busy')) {
          pending = false;
          syncModalLoading(false);
          showDialogError(
            formError?.textContent?.trim() || 'We couldn’t start the add. Please try again.',
          );
          focusDialogError();
          syncGoldCoverConfirmation();
        }
      });

      const goldCoverFormObserver = new MutationObserver(syncGoldCoverConfirmation);
      goldCoverFormObserver.observe(form, {
        attributes: true,
        attributeFilter: ['aria-busy'],
      });
      const goldCoverButtonObserver = new MutationObserver(syncGoldCoverConfirmation);
      goldCoverButtonObserver.observe(originalButton, {
        attributes: true,
        attributeFilter: ['data-gold-cover-added', 'disabled'],
      });
      if (formError instanceof HTMLElement) {
        const goldCoverErrorObserver = new MutationObserver(syncGoldCoverConfirmation);
        goldCoverErrorObserver.observe(formError, {
          attributes: true,
          attributeFilter: ['hidden', 'data-cart-recovery-error'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
      syncGoldCoverConfirmation();
      if (goldCover.dataset.goldCoverModalReady !== 'true') {
        goldCover.dataset.goldCoverModalReady = 'true';
      }
      if (originalButton.hidden) originalButton.hidden = false;
    }
  }

  drawerControllers.set(drawerType, { dialog, openDrawer, closeDrawer, syncSceneMotion });
});

document.addEventListener('visibilitychange', () => {
  drawerControllers.forEach((controller) => controller.syncSceneMotion());
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  const trigger = event.target.closest('[data-product-attribute-trigger]');

  if (trigger) {
    if (trigger.matches('[data-gold-cover-add][data-gold-cover-added]')) return;

    const controller = drawerControllers.get(trigger.dataset.productAttributeTrigger);

    if (controller) {
      event.preventDefault();
      controller.openDrawer(trigger, { immediate: event.detail === 0 });
    }
    return;
  }

  const closeControl = event.target.closest(
    '[data-product-attribute-close], [data-product-attribute-dismiss]',
  );
  const dialog = closeControl?.closest('[data-product-attribute-dialog]');
  const controller = dialog
    ? drawerControllers.get(dialog.dataset.productAttributeDialog)
    : null;

  controller?.closeDrawer({ immediate: event.detail === 0 });
});
